import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "@/shared/lib/api";
import {
  Search, ChevronDown, ChevronRight, AlertTriangle, FileWarning,
  Receipt, Banknote, MessageSquarePlus, X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * AccountsCommercials — single neat home for the Accounts team: project
 * commercial value (original / variations / revised), pending commercial
 * changes, AND receivables (invoices raised, received vs outstanding,
 * record-payment and follow-up notes — all inline, no separate page).
 */
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function AccountsCommercials() {
  const [data, setData] = useState({ totals: {}, projects: [] });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [payFor, setPayFor] = useState(null);
  const [fuFor, setFuFor] = useState(null);

  function load() {
    setLoading(true);
    api.get("/accounts/commercials")
      .then((r) => setData(r.data || { totals: {}, projects: [] }))
      .catch((e) => toast.error(fmtErr(e.response?.data?.detail) || "Load failed"))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  const totals = data.totals || {};
  const projects = data.projects || [];

  const filtered = useMemo(() => projects.filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return p.project_code.toLowerCase().includes(s)
      || (p.name || "").toLowerCase().includes(s)
      || (p.client_name || "").toLowerCase().includes(s)
      || (p.upr_no || "").toLowerCase().includes(s)
      || (p.invoices || []).some((iv) => (iv.invoice_number || "").toLowerCase().includes(s));
  }), [projects, q]);

  const pendingProjects = filtered.filter((p) => p.pending_count > 0);

  return (
    <div className="p-6 lg:p-10" data-testid="accounts-commercials-page">
      {/* Hero */}
      <div className="mb-8 rounded-3xl bg-gradient-to-br from-emerald-950 via-zinc-900 to-zinc-800 text-white p-6 sm:p-9 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-500/10 rounded-full -mr-24 -mt-24 blur-3xl" />
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">Accounts · Commercials &amp; Receivables</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Commercials &amp; Receivables</h1>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <KpiHero label="Revised Value" value={inr(totals.revised_value)} />
            <KpiHero label="Invoiced" value={inr(totals.invoiced_value)} tone="emerald" />
            <KpiHero label="Received" value={inr(totals.received_value)} tone="emerald" />
            <KpiHero label="Outstanding" value={inr(totals.outstanding_value)} tone="amber" />
          </div>
        </div>
      </div>

      {/* Pending commercial changes */}
      {pendingProjects.length > 0 && (
        <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="pending-commercial-changes">
          <div className="flex items-center gap-2 text-amber-800 font-semibold mb-3">
            <FileWarning className="w-5 h-5" /> Pending Commercial Changes
            <span className="text-xs font-mono bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">{totals.pending_count}</span>
          </div>
          <div className="space-y-2">
            {pendingProjects.map((p) => p.pending_changes.map((c) => (
              <div key={`${p.project_code}-${c.boq_id}`}
                   className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white border border-amber-200 px-4 py-2.5"
                   data-testid={`pending-change-${c.boq_id}`}>
                <div className="min-w-0">
                  <span className="font-mono text-xs text-emerald-700"><ProjRef code={p.project_code} /></span>
                  <span className="mx-2 text-zinc-300">·</span>
                  <span className="font-medium text-sm text-zinc-800">{c.material_name}</span>
                  <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border ${
                    c.type === "variation" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                    {c.type === "variation" ? "Variation" : "Scope breach"}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-zinc-500">
                    {c.type === "variation"
                      ? <>value {inr(c.amount)} · {c.has_po ? "PO uploaded — approve pending" : c.email_confirmed ? <span className="text-amber-700 font-medium">email confirmed · PO pending</span> : "awaiting PO"}</>
                      : <>over-scope ≈ {inr(c.over_scope_value)} · {c.email_confirmed ? <span className="text-amber-700 font-medium">email confirmed · amended PO pending</span> : "awaiting amended PO"}</>}
                  </span>
                  <Link to={`/projects/${p.project_code}`} className="text-xs font-medium text-emerald-700 hover:underline" data-testid={`pending-open-${c.boq_id}`}>Open →</Link>
                </div>
              </div>
            )))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4 max-w-md">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search project, UPR, client, or invoice #…" data-testid="commercials-search"
                 className="w-full h-11 pl-9 pr-3 border border-zinc-300 rounded-full text-sm focus:outline-none focus:border-emerald-500" />
        </div>
      </div>

      {/* Project list */}
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Project</th>
              <th className="text-right px-4 py-3">Revised</th>
              <th className="text-right px-4 py-3">Invoiced</th>
              <th className="text-right px-4 py-3">Received</th>
              <th className="text-right px-4 py-3">Outstanding</th>
              <th className="text-center px-4 py-3">Pending</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-zinc-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-zinc-400">No projects with commercial value yet.</td></tr>
            )}
            {filtered.map((p) => (
              <ProjectRow key={p.project_code} p={p}
                          open={!!open[p.project_code]}
                          onToggle={() => setOpen((o) => ({ ...o, [p.project_code]: !o[p.project_code] }))}
                          onPay={setPayFor} onFollowup={setFuFor} />
            ))}
          </tbody>
        </table>
      </div>

      {payFor && <RecordPaymentModal invoice={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); toast.success("Payment recorded."); }} />}
      {fuFor && <FollowupModal invoice={fuFor} onClose={() => setFuFor(null)} onSaved={() => { setFuFor(null); load(); toast.success("Follow-up logged."); }} />}
    </div>
  );
}

function ProjectRow({ p, open, onToggle, onPay, onFollowup }) {
  return (
    <>
      <tr className="border-t border-zinc-100 hover:bg-emerald-50/30 transition-colors cursor-pointer"
          onClick={onToggle} data-testid={`commercial-row-${p.project_code}`}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
            <div>
              <div className="font-mono text-xs text-emerald-700">
                {p.upr_no ? <span className="font-semibold">UPR {p.upr_no}</span> : p.project_code}
                {p.upr_no && <span className="text-zinc-400 font-normal ml-1.5">{p.project_code}</span>}
              </div>
              <div className="text-sm font-medium text-zinc-800 mt-0.5 truncate max-w-[240px]">{p.name}</div>
              <div className="text-[11px] text-zinc-400">{p.client_name || "—"}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-700">{inr(p.revised_value)}</td>
        <td className="px-4 py-3 text-right font-mono text-zinc-700">{inr(p.invoiced_value)}</td>
        <td className="px-4 py-3 text-right font-mono text-zinc-700">{inr(p.received_value)}</td>
        <td className="px-4 py-3 text-right font-mono">
          {p.outstanding_value > 0 ? <span className="text-amber-700">{inr(p.outstanding_value)}</span> : <span className="text-emerald-600">{inr(0)}</span>}
        </td>
        <td className="px-4 py-3 text-center">
          {p.pending_count > 0
            ? <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-mono font-semibold" data-testid={`commercial-pending-${p.project_code}`}>
                <AlertTriangle className="w-3 h-3" />{p.pending_count}
              </span>
            : <span className="text-emerald-600 text-xs">clear</span>}
        </td>
        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <Link to={`/projects/${p.project_code}`}
                className="inline-flex items-center px-3 h-8 rounded-full bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800"
                data-testid={`commercial-open-${p.project_code}`}>Open</Link>
        </td>
      </tr>
      {open && (
        <tr className="bg-zinc-50/60" data-testid={`commercial-lines-${p.project_code}`}>
          <td colSpan={7} className="px-4 py-4 space-y-4">
            {/* Receivables */}
            <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`receivables-${p.project_code}`}>
              <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center gap-2 text-sm font-semibold text-zinc-800">
                <Receipt className="w-4 h-4 text-emerald-600" /> Receivables
                <span className="text-[11px] font-normal text-zinc-400">· {(p.invoices || []).length} invoice(s)</span>
              </div>
              {(p.invoices || []).length === 0 ? (
                <div className="px-4 py-4 text-sm text-zinc-400">No invoices raised yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left px-3 py-2">Invoice</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Total</th>
                      <th className="text-right px-3 py-2">Received</th>
                      <th className="text-right px-3 py-2">Balance</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Last follow-up</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.invoices.map((iv) => (
                      <tr key={iv.id} className="border-t border-zinc-100" data-testid={`receivable-${iv.invoice_number}`}>
                        <td className="px-3 py-2 font-medium text-zinc-800">{iv.invoice_number}</td>
                        <td className="px-3 py-2 capitalize text-zinc-500">{iv.milestone || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{inr(iv.total)}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{inr(iv.amount_received)}</td>
                        <td className="px-3 py-2 text-right font-mono">{iv.balance > 0 ? <span className="text-amber-700">{inr(iv.balance)}</span> : inr(0)}</td>
                        <td className="px-3 py-2"><PayPill status={iv.payment_status} /></td>
                        <td className="px-3 py-2 text-zinc-500">
                          {iv.last_followup_at ? <>{(iv.last_followup_at || "").slice(0, 10)}{iv.followup_count > 1 ? ` · ${iv.followup_count}` : ""}</> : <span className="text-zinc-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {iv.balance > 0 && iv.payment_status !== "paid" && (
                            <button onClick={() => onPay({ ...iv, upr_no: p.upr_no })} data-testid={`record-payment-${iv.invoice_number}`}
                              className="inline-flex items-center gap-1 px-2 h-7 rounded-full bg-emerald-600 text-white text-[11px] font-medium hover:bg-emerald-700 mr-1.5">
                              <Banknote className="w-3 h-3" /> Record
                            </button>
                          )}
                          <button onClick={() => onFollowup({ ...iv, upr_no: p.upr_no })} data-testid={`add-followup-${iv.invoice_number}`}
                            className="inline-flex items-center gap-1 px-2 h-7 rounded-full border border-zinc-300 text-zinc-700 text-[11px] font-medium hover:bg-zinc-50">
                            <MessageSquarePlus className="w-3 h-3" /> Follow-up
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Line breakup */}
            <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-zinc-100 text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">S.No</th>
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-left px-3 py-2">Location</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Rate (₹)</th>
                    <th className="text-right px-3 py-2">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {p.lines.map((l, i) => (
                    <tr key={l.boq_id} className="border-t border-zinc-100">
                      <td className="px-3 py-2 font-mono text-zinc-500">{l.wall_no || i + 1}</td>
                      <td className="px-3 py-2">
                        <span className="font-medium text-zinc-800">{l.material_name}</span>
                        {l.is_variation && (
                          <span className={`ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[8px] font-mono uppercase ${
                            l.variation_status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-violet-50 text-violet-700 border border-violet-200"}`}>
                            {l.variation_status === "approved" ? "variation ✓" : "variation"}
                          </span>
                        )}
                        {l.scope_status === "red" && (
                          <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-rose-50 text-rose-700 border border-rose-200">breach</span>
                        )}
                        {l.email_confirmed && (
                          <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-amber-50 text-amber-700 border border-amber-200"
                            data-testid={`accounts-email-pending-${l.boq_id}`}>email confirmed · PO pending</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-zinc-600">{l.location || "—"}</td>
                      <td className="px-3 py-2 text-zinc-600">{l.unit}</td>
                      <td className="px-3 py-2 text-right font-mono">{l.quantity_required}</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(l.rate || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{Number(l.amount || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-zinc-200 bg-zinc-50 font-semibold">
                    <td colSpan={6} className="px-3 py-2 text-right">Revised Project Value</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{inr(p.revised_value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PayPill({ status }) {
  const map = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    part_paid: "bg-amber-50 text-amber-700 border-amber-200",
    partial: "bg-amber-50 text-amber-700 border-amber-200",
    pending: "bg-zinc-100 text-zinc-500 border-zinc-200",
  };
  const labelMap = { part_paid: "partial", paid: "paid", pending: "pending", partial: "partial" };
  return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium capitalize ${map[status] || map.pending}`}>{labelMap[status] || status || "pending"}</span>;
}

function KpiHero({ label, value, tone }) {
  const tones = { amber: "text-amber-300", emerald: "text-emerald-300", rose: "text-rose-300" };
  return (
    <div className="bg-white/5 rounded-2xl p-3.5 border border-white/10">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${tones[tone] || "text-white"}`}>{value}</div>
    </div>
  );
}

/* ---------------- Modals ---------------- */
function Modal({ title, subtitle, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <div className="font-semibold text-zinc-900">{title}</div>
            {subtitle && <div className="text-xs text-zinc-400 font-mono">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function RecordPaymentModal({ invoice, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const balance = invoice.balance ?? Math.max((invoice.total || 0) - (invoice.amount_received || 0), 0);
  const [form, setForm] = useState({ amount: String(balance), payment_date: today, mode: "bank", reference: "", tds: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  async function submit() {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      await api.post("/payments", {
        invoice_id: invoice.id, amount: amt, payment_date: form.payment_date,
        mode: form.mode, reference: form.reference, tds: parseFloat(form.tds || 0), remarks: form.remarks,
      });
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Could not record payment"); }
    finally { setBusy(false); }
  }

  return (
    <Modal title="Record Payment" subtitle={`${invoice.invoice_number}${invoice.upr_no ? ` · UPR ${invoice.upr_no}` : ""}`} onClose={onClose} testid="record-payment-modal">
      {invoice.upr_no && (
        <div className="mb-3 inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-mono font-semibold" data-testid="rp-upr-chip">
          UPR {invoice.upr_no}
        </div>
      )}
      <div className="grid grid-cols-3 gap-3 mb-4 p-3 rounded-xl bg-emerald-50/40 border border-emerald-100 text-center">
        <div><div className="text-[10px] font-mono uppercase text-zinc-400">Total</div><div className="font-semibold text-sm">{inr(invoice.total)}</div></div>
        <div><div className="text-[10px] font-mono uppercase text-zinc-400">Received</div><div className="font-semibold text-sm text-emerald-700">{inr(invoice.amount_received)}</div></div>
        <div><div className="text-[10px] font-mono uppercase text-zinc-400">Balance</div><div className="font-semibold text-sm text-amber-700">{inr(balance)}</div></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Lbl t="Amount Received (₹)"><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="rp-amount" className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500" /></Lbl>
        <Lbl t="Payment Date"><input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="rp-date" className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500" /></Lbl>
        <Lbl t="Mode">
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="rp-mode" className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500">
            <option value="bank">Bank Transfer</option><option value="cheque">Cheque</option><option value="cash">Cash</option><option value="upi">UPI</option>
          </select>
        </Lbl>
        <Lbl t="Reference / UTR"><input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="rp-reference" className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500" /></Lbl>
      </div>
      <Lbl t="Remarks"><textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="rp-remarks" className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500" /></Lbl>
      <div className="flex justify-end gap-2 pt-3">
        <button onClick={onClose} className="px-4 h-10 rounded-full border border-zinc-300 text-sm font-medium hover:bg-zinc-50">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="rp-submit" className="px-5 h-10 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Record Payment"}</button>
      </div>
    </Modal>
  );
}

function FollowupModal({ invoice, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!note.trim()) { toast.error("Write a follow-up note first"); return; }
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/followup`, { note: note.trim(), next_followup_date: next || null });
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Could not log follow-up"); }
    finally { setBusy(false); }
  }

  return (
    <Modal title="Add Follow-up" subtitle={invoice.invoice_number} onClose={onClose} testid="followup-modal">
      <Lbl t="Note (what was said?)">
        <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} data-testid="fu-note"
          placeholder="e.g. Called accounts contact — payment promised by 28th." className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500" />
      </Lbl>
      <Lbl t="Next follow-up date (optional)">
        <input type="date" value={next} onChange={(e) => setNext(e.target.value)} data-testid="fu-next" className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500" />
      </Lbl>
      <div className="flex justify-end gap-2 pt-3">
        <button onClick={onClose} className="px-4 h-10 rounded-full border border-zinc-300 text-sm font-medium hover:bg-zinc-50">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="fu-submit" className="px-5 h-10 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Log Follow-up"}</button>
      </div>
    </Modal>
  );
}

function Lbl({ t, children }) {
  return (
    <label className="block mt-3">
      <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">{t}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
